import { unpinFile } from '#pplx/frontend/apps/ask/queries/asset-queries/unpinFile.js';
import { ALL_ARTIFACTS_SEARCH_ENDPOINT, getPinnedFilesQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/assetsKeys.js';
import { getPinnedItemsQueryKey, getSidebarV2InitialQueryKey, } from '#pplx/frontend/apps/ask/queries/query-keys/threadKeys.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// Single-file event: defined here at its only call site rather than in a shared package.
const artifactUnpinnedEvent = EVENT_PAYLOAD;
export function useUnpinFile({ reason, source, }) {
    const queryClient = useQueryClient();
    const queryKey = getPinnedFilesQueryKey();
    const { track } = useInstrumentation();
    return useMutation({
        mutationFn: async (asset) => {
            if (!asset?.asset_id) {
                logger.log('no asset_id found to unpinFile');
                return;
            }
            return unpinFile({ assetId: asset.asset_id, reason });
        },
        onMutate: async (asset) => {
            if (!asset)
                return;
            await Promise.all([
                queryClient.cancelQueries({ queryKey }),
                queryClient.cancelQueries({ queryKey: getPinnedItemsQueryKey() }),
            ]);
            const previousPinnedFiles = queryClient.getQueriesData({ queryKey });
            const previousPinnedItems = queryClient.getQueriesData({
                queryKey: getPinnedItemsQueryKey(),
            });
            queryClient.setQueriesData({ queryKey }, (old) => {
                if (!old)
                    return [];
                return old.filter((a) => a.asset_id !== asset.asset_id);
            });
            queryClient.setQueriesData({ queryKey: getPinnedItemsQueryKey() }, (old) => old?.filter((item) => item.kind !== 'artifact' || item.artifact.asset_id !== asset.asset_id));
            return { previousPinnedFiles, previousPinnedItems };
        },
        onError: (_error, _asset, context) => {
            for (const [cachedQueryKey, data] of context?.previousPinnedFiles ?? []) {
                queryClient.setQueryData(cachedQueryKey, data);
            }
            for (const [key, data] of context?.previousPinnedItems ?? []) {
                queryClient.setQueryData(key, data);
            }
        },
        onSuccess: (_data, asset) => {
            if (!asset?.asset_id)
                return;
            const pinnedFiles = queryClient.getQueryData(queryKey) ?? [];
            track(artifactUnpinnedEvent({
                name: 'artifact_unpinned',
                data: {
                    artifact_id: asset.asset_id,
                    source,
                    new_pin_count: pinnedFiles.length,
                },
            }));
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries({ queryKey: getPinnedItemsQueryKey(), exact: false });
            queryClient.invalidateQueries({ queryKey: getSidebarV2InitialQueryKey() });
            queryClient.invalidateQueries({
                predicate: QueryClientHelper.predicateForKeys([ALL_ARTIFACTS_SEARCH_ENDPOINT]),
            });
        },
    });
}
//# sourceMappingURL=useUnpinFile.js.map